// What one `daemon_status` notice should cost (CYBORG-1205 item 5). The relay sends
// one copy per workspace the viewer shares with the daemon (4 live), and every online
// edge refetched `list_agents` (36 KB) in every open tab although the tab already held
// that daemon's rows. The notice itself carries the change: apply it; read the roster on
// the daemon's first edge this page load, or when this tab holds none of its agents.

export type DaemonNoticeAction = "ignore" | "apply" | "refetch-roster";

export function daemonNoticeAction(
  prev: string | undefined,
  status: string,
  daemonId: string,
  heldAgents: ReadonlyArray<{ daemonId?: string | null }>,
  seenOnlineEdge: boolean,
): DaemonNoticeAction {
  if (prev === status) return "ignore";
  if (status !== "online") return "apply";
  // Before this page load's first online edge, held rows came from the cold-paint cache or
  // the relay's offline mirror (which drops sessions with no owner email), not the daemon.
  if (!seenOnlineEdge) return "refetch-roster";
  return heldAgents.some((a) => a.daemonId === daemonId) ? "apply" : "refetch-roster";
}

// CYBORG-1210: the online edge of a daemon whose rows this tab does not hold (the owner's
// own computer coming back, 36.7 KB in every tab) reads that daemon's rows only, then
// replaces them here. A silent daemon (still pending) changes nothing.
export function mergeDaemonRows<T extends { agentId?: string; daemonId?: string | null }>(
  current: T[],
  daemonId: string,
  answer: T[],
  pendingDaemons: readonly string[],
): T[] {
  if (pendingDaemons.includes(daemonId)) return current;
  const answered = new Set(answer.map((a) => a.agentId));
  const kept = current.filter((a) => a.daemonId !== daemonId && !answered.has(a.agentId));
  return [...kept, ...answer];
}
